import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { useDeliveryUpdater, useSession } from "@/lib/queries";
import { AiPanel, AiSource } from "@/components/common/AiPanel";

const EXAMPLE_EMAIL = `Hei!

Vi holder på å bygge om kjelleren i huset vårt i Skudeneshavn og trenger ny trapp ned fra gangen. Det er ca. 2,5 meter fra gulv til gulv. Vi ønsker en åpen trapp i eik, gjerne med glassrekkverk hvis det ikke blir alt for dyrt. Trappen må svinge til venstre nederst.

Vi håper på levering i løpet av mai. Kan dere komme på befaring?

Mvh
Siri og Thomas Hovland
tlf 95 00 00 00`;

const emptyDraft = () => ({
  customer: { name: "", type: "privat", phone: "", email: "", address: "" },
  project_name: "",
  building_type: "",
  wishes: [],
  desired_text: "",
  uncertainties: [],
  summary: "",
  origin: "manuell",
});

export const NewCaseDialog = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const update = useDeliveryUpdater();
  const { data: session } = useSession();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [sources, setSources] = useState({});
  const aiOn = session?.ai?.configured;

  const reset = () => { setText(""); setDraft(null); setSources({}); };

  const runAi = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/ai/extract-case", { text });
      const s = data.suggestion;
      setDraft({
        customer: { name: s.kunde?.navn || "", type: s.kunde?.kundetype === "entreprenor" ? "entreprenor" : s.kunde?.kundetype === "borettslag" ? "borettslag" : "privat", phone: s.kunde?.telefon || "", email: s.kunde?.epost || "", address: s.kunde?.adresse || "" },
        project_name: s.prosjekt?.navn || "",
        building_type: s.prosjekt?.bygningstype || "",
        wishes: (s.trappeonsker || []).map((t) => ({ label: t.felt, value: t.verdi, source: t.kilde })),
        desired_text: s.onsket_levering?.tekst || "",
        uncertainties: s.uavklart || [],
        summary: s.oppsummering || "",
        origin: "ki",
      });
      setSources({ customer: s.kunde?.kilde, project: s.prosjekt?.kilde, delivery: s.onsket_levering?.kilde });
    } catch (e) {
      toast.error(errorMessage(e, "KI-analysen feilet"));
    } finally {
      setBusy(false);
    }
  };

  const manual = () => { setDraft({ ...emptyDraft(), origin: "manuell" }); setSources({}); };

  const save = async () => {
    if (!draft.project_name.trim() || !draft.customer.name.trim()) {
      toast.error("Prosjektnavn og kundenavn må fylles ut.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post("/deliveries", {
        project_name: draft.project_name,
        customer: draft.customer,
        building_type: draft.building_type || null,
        stair_type: draft.wishes.find((w) => w.label.toLowerCase().includes("trappetype"))?.value || null,
        stair_summary: draft.wishes.slice(0, 3).map((w) => w.value).join(" · ") || null,
        fields: draft.wishes.filter((w) => w.label && w.value).map((w) => ({ key: w.label.toLowerCase().replace(/[^a-zæøå0-9]+/g, "_"), label: w.label, value: w.value, group: "Kundevalg" })),
        desired_text: draft.desired_text || null,
        inquiry_text: text || null,
        uncertainties: draft.uncertainties.filter(Boolean),
        source: draft.origin,
      });
      update(data);
      toast.success(`Forespørsel ${data.number} er opprettet`);
      onOpenChange(false);
      reset();
      navigate(`/leveranser/${data.id}`);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const setWish = (i, patch) => setDraft((d) => ({ ...d, wishes: d.wishes.map((w, j) => (j === i ? { ...w, ...patch } : w)) }));

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ny forespørsel</DialogTitle>
          <DialogDescription>Lim inn henvendelsen fra kunden. KI foreslår kundeinfo, trappeønsker og uavklarte punkter – du kontrollerer alt før forespørselen opprettes.</DialogDescription>
        </DialogHeader>

        {!draft && (
          <div className="space-y-3">
            <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} placeholder="Lim inn henvendelsen her…" data-testid="new-case-email-input" />
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" onClick={() => setText(EXAMPLE_EMAIL)} data-testid="new-case-insert-example">Sett inn eksempelhenvendelse</Button>
              <Button onClick={runAi} disabled={busy || text.trim().length < 20 || !aiOn} data-testid="new-case-ai-button">
                {busy && <Loader2 className="animate-spin" />} Lag utkast med KI
              </Button>
              <Button variant="ghost" onClick={manual} data-testid="new-case-manual-button">Fyll ut manuelt</Button>
            </div>
            {!aiOn && <p className="text-xs text-muted-foreground">KI er ikke konfigurert i dette miljøet. Fyll ut manuelt.</p>}
          </div>
        )}

        {draft && (
          <div className="space-y-4">
            {draft.origin === "ki" && (
              <AiPanel title="Utkast til forespørsel" meta="Alle felt kan redigeres">
                {draft.summary && <p>{draft.summary}</p>}
                {draft.uncertainties.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold">Uavklart – må følges opp med kunden</p>
                    <ul className="list-disc pl-5 text-sm">{draft.uncertainties.map((u, i) => <li key={i}>{u}</li>)}</ul>
                  </div>
                )}
              </AiPanel>
            )}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="nc-name">Kunde</Label>
                <Input id="nc-name" value={draft.customer.name} onChange={(e) => setDraft({ ...draft, customer: { ...draft.customer, name: e.target.value } })} data-testid="new-case-customer-name" />
                <AiSource quote={sources.customer} />
              </div>
              <div className="space-y-1.5">
                <Label>Kundetype</Label>
                <Select value={draft.customer.type} onValueChange={(v) => setDraft({ ...draft, customer: { ...draft.customer, type: v } })}>
                  <SelectTrigger data-testid="new-case-customer-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="privat">Privat</SelectItem><SelectItem value="entreprenor">Entreprenør</SelectItem><SelectItem value="borettslag">Borettslag</SelectItem></SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label htmlFor="nc-phone">Telefon</Label><Input id="nc-phone" value={draft.customer.phone} onChange={(e) => setDraft({ ...draft, customer: { ...draft.customer, phone: e.target.value } })} data-testid="new-case-customer-phone" /></div>
              <div className="space-y-1.5"><Label htmlFor="nc-email">E-post</Label><Input id="nc-email" value={draft.customer.email} onChange={(e) => setDraft({ ...draft, customer: { ...draft.customer, email: e.target.value } })} /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label htmlFor="nc-addr">Adresse / sted</Label><Input id="nc-addr" value={draft.customer.address} onChange={(e) => setDraft({ ...draft, customer: { ...draft.customer, address: e.target.value } })} /></div>
              <div className="space-y-1.5">
                <Label htmlFor="nc-project">Prosjektnavn</Label>
                <Input id="nc-project" value={draft.project_name} onChange={(e) => setDraft({ ...draft, project_name: e.target.value })} placeholder="F.eks. Kjellertrapp Hovland" data-testid="new-case-project-name" />
                <AiSource quote={sources.project} />
              </div>
              <div className="space-y-1.5"><Label htmlFor="nc-building">Bygningstype</Label><Input id="nc-building" value={draft.building_type} onChange={(e) => setDraft({ ...draft, building_type: e.target.value })} /></div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label htmlFor="nc-delivery">Ønsket levering (slik kunden skrev det)</Label>
                <Input id="nc-delivery" value={draft.desired_text} onChange={(e) => setDraft({ ...draft, desired_text: e.target.value })} data-testid="new-case-desired" />
                <AiSource quote={sources.delivery} />
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between">
                <Label>Trappeønsker (kundens egne ord – ikke bekreftet spesifikasjon)</Label>
                <Button variant="ghost" size="sm" onClick={() => setDraft({ ...draft, wishes: [...draft.wishes, { label: "", value: "", source: null }] })} data-testid="new-case-add-wish"><Plus /> Legg til</Button>
              </div>
              <div className="space-y-2">
                {draft.wishes.map((w, i) => (
                  <div key={i} className="rounded-lg border p-2" data-testid={`new-case-wish-${i}`}>
                    <div className="grid grid-cols-[1fr_2fr_auto] gap-2">
                      <Input value={w.label} onChange={(e) => setWish(i, { label: e.target.value })} placeholder="Felt" />
                      <Input value={w.value} onChange={(e) => setWish(i, { value: e.target.value })} placeholder="Verdi" />
                      <Button variant="ghost" size="icon" aria-label="Fjern" onClick={() => setDraft({ ...draft, wishes: draft.wishes.filter((_, j) => j !== i) })}><Trash2 /></Button>
                    </div>
                    <AiSource quote={w.source} />
                  </div>
                ))}
                {!draft.wishes.length && <p className="text-sm text-muted-foreground">Ingen ønsker registrert.</p>}
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-2 border-t pt-4">
              <Button variant="ghost" onClick={() => setDraft(null)} data-testid="new-case-back">Tilbake</Button>
              <Button onClick={save} disabled={saving} data-testid="new-case-create-button">{saving && <Loader2 className="animate-spin" />} Opprett forespørsel</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
